import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAutoRefresh, AUTO_REFRESH_MS } from "../../../hooks/useAutoRefresh";
import { useNavigate } from "react-router-dom";
import { Alert, Col, Row, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { fetchPosTransactions } from "../../../api/requests/pos-reconciliation-requests";
import { fetchPaymentHistory } from "../../../api/requests/payment-requests";
import PaymentDetailPanel from "../../payment/PaymentDetailPanel";
import { POS_REPORT_SCOPE, getPosReportStyles } from "./PosReportStyles";
import {
    Loading,
    ORANGE,
    Pager,
    PendingCell,
    EstimatedTag,
    SortableTh,
    StatCard,
    TableCard,
    fmtDate,
    fmtDateTime,
    fmtMoney2,
    fmtPct,
    providerLabelOf,
    theadStyle,
    trNum,
    usePosSort,
} from "./PosReportShared";

/* ============================================================================
 * İşlem bazlı tablo — POS Rapor hiyerarşisinin yaprak görünümü.
 *
 * Aynı bileşen iki yerde kullanılır:
 *   - Firma detayındaki "İşlem Bazlı" sekmesi   (provider + companyId)
 *   - Cari detayındaki İşlem Detayı ekranı      (provider + companyId + accountId)
 *
 * Kapsam daraltması backend'de yapılır; burada hiçbir filtreleme yoktur.
 *
 * Satıra (veya Detay ikonuna) tıklanınca ödeme detayı sağdan açılan panelde
 * gösterilir — Ödeme Geçmişi ekranıyla aynı bileşen ve stil kaynağı kullanılır.
 * ========================================================================== */

// Stil metni sabit; her render'da yeniden üretilirse React <style> düğümünü
// tazeler ve tarayıcı tüm kuralları yeniden ayrıştırır (panel açarken takılma sebebi).
const POS_REPORT_STYLES = getPosReportStyles();

/** Tıklanabilir satırın üzerine gelindiğindeki gri vurgu. */
const HOVER_BG = "#f6f7fb";

/** Bir sayfada gösterilen işlem sayısı (kapsam tablolarıyla aynı). */
const PAGE_SIZE = 20;

/** Ad kolonları kapsam tablolarıyla aynı genişlikte kalır; taşan metin "…" olur. */
const NAME_CELL = { maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

/**
 * Bu tablonun gelişmiş filtre alanları — filtre çubuğu sayfada olduğu için
 * tanım buradan dışa verilir. Kart Tipi yalnız SUPER_ADMIN'e gösterilir.
 */
export const TRANSACTION_FILTER_FIELDS = (isSuperAdmin = false) => [
    { key: "search", type: "text", label: "Cari / İşlem No / Kart",
      placeholder: "Cari adı, işlem no veya kart" },
    { key: "amount", type: "range", label: "İşlem Tutarı (₺)",
      minKey: "minAmount", maxKey: "maxAmount" },
    { key: "valorDate", type: "range", label: "Valör Tarihi", inputType: "date",
      minKey: "minValorDate", maxKey: "maxValorDate" },
    { key: "installmentType", type: "select", label: "Taksit",
      options: [{ value: "single", label: "Tek Çekim" }, { value: "multi", label: "Taksitli" }] },
    ...(isSuperAdmin
        ? [{ key: "cardType", type: "select", label: "Kart Tipi",
             options: [{ value: "DEBIT", label: "Debit" }, { value: "CREDIT", label: "Credit" }] }]
        : []),
];


/**
 * İşlemde uygulanan komisyon oranı.
 *
 * Eskiden "anlaşılan" (sözleşme) orana göre kırmızı/yeşil boyanırdı; anlaşılan oranı
 * giren arayüz kaldırıldığı için kıyas edilecek referans kalmadı ve renk her zaman
 * yeşile düşüyordu — yanıltıcı olmaması için düz gösteriliyor.
 */
const AppliedRate = ({ row }) => {
    if (row.commissionPercent == null) return <PendingCell hint="POS oran konfigürasyonu bulunamadı" />;
    return <span className="fw-bold">{fmtPct(row.commissionPercent)}</span>;
};

export default function PosTransactionsTable({
    provider,
    companyId,
    accountId,
    startDate,
    endDate,
    isSuperAdmin = false,
    showSummary = true,
    filters,
}) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    const [pageInfo, setPageInfo] = useState({ page: 0, totalPages: 0, totalElements: 0, totals: null });
    const [page, setPage] = useState(0);
    // Sıralama, filtreleme ve sayfalama sunucuda: liste sayfalı geldiği için
    // kolon tıklaması görünen 20 satırı değil tüm kapsamı sıralamalı.
    const filtersActive = Object.keys(filters || {}).length > 0;
    const { sort, direction, toggle } = usePosSort(null, "desc");
    const onSort = (key) => { setPage(0); toggle(key); };
    const Th = (props) => <SortableTh {...props} sort={sort} direction={direction} onSort={onSort} />;

    const [openingId, setOpeningId] = useState(null);
    const [hoveredId, setHoveredId] = useState(null);
    // Sağdan açılan detay panelinde gösterilen ödeme (null ise panel kapalı)
    const [detailPayment, setDetailPayment] = useState(null);

    /**
     * Satırın ödeme kaydını çekip sağ detay panelinde açar. POS işlem satırı
     * panelin beklediği tüm alanları taşımadığı için kayıt ödeme geçmişi
     * ucundan merchantOid ile alınır (tekil ödeme getiren bir uç yoktur).
     * Aynı satıra tekrar tıklanırsa panel kapanır.
     */
    const openDetail = useCallback(async (row) => {
        if (detailPayment?.id === row.paymentId) {
            setDetailPayment(null);
            return;
        }
        setOpeningId(row.paymentId);
        try {
            const res = await fetchPaymentHistory({ searchTerm: row.merchantOid, page: 0, size: 5 });
            const payment = res?.data?.content?.find((p) => p.id === row.paymentId);
            if (payment) setDetailPayment(payment);
            else toast.error("Ödeme detayı bulunamadı.");
        } catch (e) {
            toast.error("Ödeme detayı alınamadı.");
        } finally {
            setOpeningId(null);
        }
    }, [detailPayment]);

    const closeDetail = useCallback(() => setDetailPayment(null), []);

    // Sağ panelin sticky kalabilmesi için tema kısıtını yalnızca bu ekranda gevşetiyoruz
    // (bkz. modernTableStyles → body.<scope>-page .main-content).
    useEffect(() => {
        const cls = `${POS_REPORT_SCOPE}-page`;
        document.body.classList.add(cls);
        return () => document.body.classList.remove(cls);
    }, []);

    // Kapsam/tarih/filtre değişince açık panel ilgisiz kalır; kapatılır ve başa dönülür.
    useEffect(() => {
        setDetailPayment(null);
        setPage(0);
    }, [provider, companyId, accountId, startDate, endDate, filters]);

    /**
     * @param {{silent?: boolean}} [opts] silent=true otomatik yenilemede kullanılır:
     * tam sayfa spinner'ı açılmaz ve hata mesajı basılmaz, böylece ekrandaki veri
     * ve kullanıcının filtre/sayfa durumu olduğu gibi kalır.
     */
    const load = useCallback(async ({ silent = false } = {}) => {
        if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const res = await fetchPosTransactions(provider, startDate, endDate, companyId, accountId, {
                page, size: PAGE_SIZE, sort, direction, ...(filters || {}),
            });
            if (res?.statusCode === 200 && res.data) {
                setRows(res.data.content || []);
                setPageInfo({
                    page: res.data.page ?? 0,
                    totalPages: res.data.totalPages ?? 0,
                    totalElements: res.data.totalElements ?? 0,
                    totals: res.data.totals || null,
                });
            } else if (!silent && res?.statusCode === 403) setError("Bu veriyi görüntüleme yetkiniz yok.");
            else if (!silent) setError(res?.message || "İşlem verisi alınamadı.");
        } catch (e) {
            if (!silent) setError("Veri yüklenirken bir hata oluştu.");
        } finally {
            if (!silent) setLoading(false);
        }
    }, [provider, companyId, accountId, startDate, endDate, page, sort, direction, filters]);

    // Otomatik yenileme: load useCallback'i o anki filtre/sayfa/sıralamayı zaten
    // kapsadığı için sessiz çağrı aynı süzgeçle tazeler. Detay paneli açıkken de
    // sorun olmaz; panel seçili satırın kopyasını tutar.
    useAutoRefresh(() => load({ silent: true }), { intervalMs: AUTO_REFRESH_MS });

    useEffect(() => {
        load();
    }, [load]);

    // Toplamlar sunucudan gelir ve görünen sayfayı değil filtrelenmiş kümenin
    // tamamını yansıtır — sayfa değiştirince değişmemeleri gerekir.
    const totals = useMemo(() => {
        const t = pageInfo.totals;
        return {
            count: t?.transactionCount ?? 0,
            gross: Number(t?.amount ?? 0),
            commission: Number(t?.commissionAmount ?? 0),
            refunded: Number(t?.refundedAmount ?? 0),
            net: Number(t?.netAmount ?? 0),
        };
    }, [pageInfo]);

    if (loading) return <Loading />;

    return (
        <div className={POS_REPORT_SCOPE}>
            <style>{POS_REPORT_STYLES}</style>
            {error && <Alert color="danger">{error}</Alert>}

            <Row className="g-3 pos-report-row">
                <Col xl={detailPayment ? 8 : 12} xxl={detailPayment ? 9 : 12} className="ph-main">
                    {showSummary && (
                        <Row className="g-3 mb-3">
                            <Col xs={6} md={3}>
                                <StatCard accent label="İşlem Tutarı" value={fmtMoney2(totals.gross)}
                                          sub={`${trNum(totals.count)} işlem`} />
                    </Col>
                    <Col xs={6} md={3}>
                        <StatCard label="Komisyon Tutarı" value={fmtMoney2(totals.commission)} />
                    </Col>
                    <Col xs={6} md={3}>
                        <StatCard label="İptal / İade Toplamı" value={fmtMoney2(totals.refunded)} />
                    </Col>
                    <Col xs={6} md={3}>
                        <StatCard label="Net Tutar" value={fmtMoney2(totals.net)} />
                    </Col>
                </Row>
            )}

            <TableCard>
                <table className="table align-middle mb-0" style={{ whiteSpace: "nowrap" }}>
                    <thead>
                        <tr style={theadStyle}>
                            <Th sortKey="accountName">Cari Adı</Th>
                            <Th sortKey="companyName">Firma Adı</Th>
                            <Th sortKey="merchantOid">İşlem No</Th>
                            <Th sortKey="paymentDate">İşlem Tarihi</Th>
                            <Th sortKey="valorDate">Valör Tarihi</Th>
                            <Th sortKey="amount" className="text-center">İşlem Tutarı</Th>
                            <Th sortKey="refundedAmount" className="text-center">İptal / İade Toplamı</Th>
                            <Th sortKey="commissionPercent" className="text-center">İşlem Komisyon Oranı</Th>
                            <Th sortKey="commissionAmount" className="text-center">Komisyon Tutarı</Th>
                            <Th sortKey="netAmount" className="text-center">Net Tutar</Th>
                            <Th sortKey="installmentCount">Taksit Sayısı</Th>
                            <Th sortKey="cardBrand">İşlem Yapılan Kart</Th>
                            {isSuperAdmin && <Th sortKey="cardType">Kart Tipi</Th>}
                            <Th sortKey="provider">POS Sağlayıcı</Th>
                            <th className="border-0 text-center">Detay</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={isSuperAdmin ? 15 : 14} className="text-center text-muted py-4">
                                    {filtersActive
                                        ? "Filtrelere uyan işlem bulunmuyor."
                                        : "Bu dönemde bu kapsamda işlem bulunmuyor."}
                                </td>
                            </tr>
                        ) : (
                            rows.map((r) => (
                                <tr
                                    key={r.paymentId}
                                    style={{
                                        borderTop: "1px solid #f0f2f6",
                                        cursor: "pointer",
                                        background: hoveredId === r.paymentId
                                            ? HOVER_BG
                                            : (detailPayment?.id === r.paymentId ? "#eef2f9" : undefined),
                                    }}
                                    onClick={() => openDetail(r)}
                                    onMouseEnter={() => setHoveredId(r.paymentId)}
                                    onMouseLeave={() => setHoveredId(null)}
                                >
                                    <td className="fw-semibold text-dark" style={NAME_CELL} title={r.accountName || "Carisiz"}>
                                        {r.accountName || "Carisiz"}
                                    </td>
                                    <td className="text-muted" style={NAME_CELL} title={r.companyName || ""}>
                                        {r.companyName || "—"}
                                    </td>
                                    <td className="text-muted">{r.merchantOid || "—"}</td>
                                    <td className="text-muted">{fmtDateTime(r.paymentDate)}</td>
                                    <td className="text-muted">{fmtDate(r.valorDate)}</td>
                                    <td className="text-center fw-bold text-dark">{fmtMoney2(r.amount)}</td>
                                    <td className="text-center">
                                        {Number(r.refundedAmount) > 0 ? fmtMoney2(r.refundedAmount) : fmtMoney2(0)}
                                    </td>
                                    <td className="text-center"><AppliedRate row={r} /></td>
                                    <td className="text-center" style={{ color: ORANGE }}>
                                        {fmtMoney2(r.commissionAmount)}
                                        {r.commissionEstimated && <EstimatedTag />}
                                    </td>
                                    <td className="text-center fw-semibold">{fmtMoney2(r.netAmount)}</td>
                                    <td>{!r.installmentCount || r.installmentCount === 1 ? "Tek Çekim" : `${r.installmentCount} Taksit`}</td>
                                    <td>{r.cardBrand || "—"}</td>
                                    {isSuperAdmin && (
                                        <td>
                                            {r.cardType
                                                ? (r.cardType === "DEBIT" ? "Debit" : "Credit")
                                                : <PendingCell hint="Kart tipi verisi henüz toplanmıyor" />}
                                        </td>
                                    )}
                                    <td>{r.providerLabel || providerLabelOf(r.provider)}</td>
                                    <td className="text-center">
                                        <button
                                            type="button"
                                            className="btn btn-sm p-0 border-0 bg-transparent"
                                            title="Ödeme detayına git"
                                            style={{ color: "#8a93a6" }}
                                            disabled={openingId === r.paymentId}
                                            onClick={(ev) => { ev.stopPropagation(); openDetail(r); }}
                                        >
                                            {openingId === r.paymentId ? (
                                                <Spinner size="sm" />
                                            ) : (
                                                <i className="bx bx-menu" style={{ fontSize: "1.2rem" }} />
                                            )}
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    {rows.length > 0 && (
                        <tfoot>
                            <tr className="fw-bold" style={{ borderTop: "2px solid #0A316E" }}>
                                <td>TOPLAM</td>
                                <td /><td /><td /><td />
                                <td className="text-center">{fmtMoney2(totals.gross)}</td>
                                <td className="text-center">{fmtMoney2(totals.refunded)}</td>
                                <td /><td />
                                <td className="text-center">{fmtMoney2(totals.commission)}</td>
                                <td className="text-center">{fmtMoney2(totals.net)}</td>
                                <td /><td />
                                {isSuperAdmin && <td />}
                                <td /><td />
                            </tr>
                        </tfoot>
                    )}
                </table>
                <Pager
                    page={pageInfo.page}
                    totalPages={pageInfo.totalPages}
                    totalElements={pageInfo.totalElements}
                    onChange={setPage}
                />
            </TableCard>
            <div className="text-muted mt-2" style={{ fontSize: ".75rem" }}>
                Komisyon oranı ve valör tarihi güncel POS konfigürasyonundan hesaplanır;
                konfigürasyon bulunmayan işlemlerde "—" gösterilir.
            </div>
                </Col>

                {/* Seçilen işlemin ödeme detayı — xl'de sağda sabit, altında sağdan açılan overlay */}
                {detailPayment && (
                    <>
                        <div className="pd-backdrop d-xl-none" onClick={closeDetail} />
                        <Col xl={4} xxl={3} className="ph-side">
                            <PaymentDetailPanel
                                payment={detailPayment}
                                onClose={closeDetail}
                                permissions={{ isSuperAdmin: false }}
                                actions={{
                                    // Makbuz/e-posta/iade akışları Ödeme Geçmişi ekranına ait;
                                    // buradan tam detay sayfasına geçilir (aynı state deseni).
                                    onOpenFullDetail: (p) => navigate(`/payment/${p.id}`, { state: { payment: p } }),
                                }}
                            />
                        </Col>
                    </>
                )}
            </Row>
        </div>
    );
}
